import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import type { AfterViewInit, OnInit } from '@angular/core';
import { Component, Inject, Input, PLATFORM_ID } from '@angular/core';
import { FormsModule } from '@angular/forms';
import type { ComponentTheme, ThemeInterface } from '@core-mkt/services/theme-parser/theme-parser.service';
import { ThemeParserService } from '@core-mkt/services/theme-parser/theme-parser.service';
import { WysiwygRedactorService } from '@core-mkt/services/wysiwg-redactor/wysiwyg-redactor.service';
import type { Accordion } from '../../accordion/accordion/accordion';
import { AccordionComponent } from '../../accordion/accordion/accordion.component';
import type { TabInfo } from './tabbed-content';
import { TabContent } from './tabbed-content';
import { TabbedContentSeoService } from './tabbed-content-seo.service';
import { SchemaService } from '@core-mkt/services/schema/schema.service';
import { getTabbedContentSchema } from './tabbed-content.schema';

/**
 * Type guard to check if a value is a SafeValue object
 * SafeValue objects have a private property 'changingThisBreaksApplicationSecurity'
 * that contains the actual HTML content
 */
interface SafeValue {
  changingThisBreaksApplicationSecurity?: string;
}

function isSafeValue(value: unknown): value is SafeValue {
  return (
    value !== null &&
    value !== undefined &&
    typeof value === 'object' &&
    'changingThisBreaksApplicationSecurity' in value
  );
}

@Component({
  selector: 'tabbed-content',
  templateUrl: './tabbed-content.component.html',
  styleUrls: ['./tabbed-content.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, AccordionComponent],
})
export class TabComponent implements OnInit, AfterViewInit {
  @Input() data: TabContent;
  @Input() enableSeoOptimizations = true; // Allow disabling SEO optimizations
  activeIndex = 0;
  theme: ThemeInterface;
  firstTabTitle = '<h3>How It Works</h3>';
  secondTabTitle = '<h3>FAQ</h3>';
  tabs: TabInfo[] = [];
  private isBrowser: boolean;
  // getCleanText is bound in the template; parse each string once rather than on every check.
  private cleanTextCache = new Map<string, string>();

  get isSeoOptimizationEnabled(): boolean {
    return this.enableSeoOptimizations && this.isBrowser;
  }

  constructor(
    private redactor: WysiwygRedactorService,
    private themeParser: ThemeParserService,
    private seoService: TabbedContentSeoService,
    private schemaService: SchemaService,
    @Inject(PLATFORM_ID) platformId: object,
    @Inject(DOCUMENT) private document: Document,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  get tabbedContentTitle(): string {
    return this.data.tabbedContentTitle;
  }

  ngOnInit(): void {
    this.loadTabs();
    const themeInput: ComponentTheme = {
      styles: this.data.styles,
      backgroundColor: this.data.backgroundColor,
      backgroundImage: null,
      textColor: '',
      columnCount: 0,
      columnDistribution: '',
    };
    this.theme = this.themeParser.getThemeClasses(themeInput);

    // Find the active tab index
    for (let i = 0; i < this.tabs.length; i++) {
      if (this.tabs[i].isActive) {
        this.activeIndex = i;
        break;
      }
    }

    // Sanitize title for SSR
    if (this.data.tabbedContentTitle) {
      this.data.tabbedContentTitle = this.redactor.bypassSanitizer(this.data.tabbedContentTitle);
    }

    // Apply SEO optimizations only in browser, and only when the kill-switch allows it.
    if (this.isSeoOptimizationEnabled && this.data) {
      this.seoService.optimizeSeoMetadata(this.data, this.tabs[this.activeIndex]);
    }
  }

  ngAfterViewInit(): void {
    // Register FAQ schema for SSR — SchemaService is a no-op in the browser.
    // Browser-side ld+json injection is handled exclusively by ServerSchemaService (SSR)
    // to keep exactly one <script type="application/ld+json"> in <head>.
    // Respect the enableSeoOptimizations kill-switch (independent of isBrowser, since
    // this runs SSR-side) so disabling SEO suppresses JSON-LD too.
    if (!this.enableSeoOptimizations) return;
    this.schemaService.register(getTabbedContentSchema(this.data, this.document.URL));
  }

  loadTabs(): void {
    const accordionData: Accordion = {
      styles: [],
      accordionTitle: null,
      accordionDisplayType: 'simple',
      buttonType: null,
      buttonLabel: null,
      buttonLink: null,
      buttonLinkNewTab: false,
      buttonLinkNofollow: false,
      accordionLeftContent: null,
      accordionItems: [],
    };

    if (this.data.tab.length === 0) {
      const newTabItem: TabInfo = {
        tabTitle: this.firstTabTitle,
        tabContentList: [],
        accordion: accordionData,
        isActive: false,
      };
      this.parseForTabList(newTabItem);
    } else {
      this.data.tab.forEach((tabItem, idx) => {
        this.parseForTabList(tabItem, idx);
      });
    }
  }

  parseForTabList(tabItem: TabInfo, idx: number = 0): void {
    if (idx === 0 && (!tabItem.tabTitle || (tabItem.tabTitle && tabItem.tabTitle.length == 0))) {
      tabItem.tabTitle = this.firstTabTitle;
    }
    if (idx === 1 && (!tabItem.tabTitle || (tabItem.tabTitle && tabItem.tabTitle.length == 0))) {
      tabItem.tabTitle = this.secondTabTitle;
    }
    let accordionArray = tabItem.tabContentList;
    if (!accordionArray || accordionArray.length === 0) {
      // Legacy tabAccordionN fields hold the section title as a plain string.
      const legacyItems = this.data[`tabAccordion${idx + 1}` as `tabAccordion${1 | 2 | 3 | 4}`] ?? [];
      accordionArray = legacyItems.map(({ accordionSectionTitle, ...item }) => ({
        ...item,
        accordionSectionTitle: [{ sectionTitle: accordionSectionTitle }],
        accordionImage: [],
      }));
    }
    tabItem.showContent = accordionArray.length > 0;
    tabItem.accordion = {
      styles: this.data.styles,
      accordionTitle: null,
      accordionDisplayType: 'simple',
      buttonType: null,
      buttonLabel: null,
      buttonLink: null,
      buttonLinkNewTab: false,
      buttonLinkNofollow: false,
      accordionLeftContent: null,
      accordionItems: accordionArray,
    };
    tabItem.tabTitle = this.redactor.bypassSanitizer(tabItem.tabTitle as string);

    this.tabs.push(tabItem);
  }

  onChange(index: string): void {
    this.activeIndex = parseInt(index, 10);
  }

  selectTab(index: number): void {
    this.activeIndex = index;

    // Update SEO metadata when tab changes
    if (this.isBrowser && this.enableSeoOptimizations) {
      this.seoService.optimizeSeoMetadata(this.data, this.tabs[index]);
    }
  }

  /**
   * Extracts raw HTML string from SafeValue objects
   * WysiwygRedactorService.bypassSanitizer() returns SafeHtml objects incorrectly cast as strings.
   * We access the internal property to get the actual HTML for text processing (SEO, accessibility).
   * Fixing the service would require refactoring 100+ components, which is outside the scope of this bugfix.
   */
  private extractHtmlFromSafeValue(html: unknown): string {
    if (!html) return '';

    // If it's already a string, return it
    if (typeof html === 'string') return html;

    // Use type guard to safely check for SafeValue
    if (isSafeValue(html) && html.changingThisBreaksApplicationSecurity) {
      return html.changingThisBreaksApplicationSecurity;
    }

    return '';
  }

  /**
   * Helper method to clean HTML from text for SEO purposes
   * Extracts plain text from SafeValue objects or HTML strings
   * SSR-safe: Only runs in browser environment
   * @param html - Can be string, SafeHtml, or any legacy type from existing code
   */
  getCleanText(html: unknown): string {
    const htmlString = this.extractHtmlFromSafeValue(html);
    if (!htmlString) return '';

    // SSR protection: document is not available on server
    if (!this.isBrowser) {
      // In SSR, use simple regex-based HTML stripping
      return htmlString.replace(/<[^>]*>/g, '');
    }

    let text = this.cleanTextCache.get(htmlString);
    if (text === undefined) {
      // Parse into a separate document so CMS markup never touches the live one. Scripts and event
      // handlers (e.g. <img onerror>) stay inert there.
      text = new DOMParser().parseFromString(htmlString, 'text/html').body.textContent ?? '';
      this.cleanTextCache.set(htmlString, text);
    }
    return text;
  }

  /**
   * Helper method to get clean tab title (plain text)
   */
  getCleanTabTitle(tabTitle: unknown): string {
    if (!tabTitle) return '';
    return this.getCleanText(tabTitle);
  }

  /**
   * Get aria-label for navigation
   * SSR-safe method that handles SafeValue objects
   */
  getAriaLabel(): string {
    if (!this.data?.tabbedContentTitle) {
      return 'Navigation for tabbed content';
    }

    const cleanText = this.getCleanText(this.data.tabbedContentTitle);
    return `Navigation for ${cleanText || 'tabbed content'}`;
  }

  /**
   * Helper method to extract HTML from SafeValue objects for SEO
   * Preserves HTML structure while avoiding SafeValue binding errors
   */
  getCleanHtml(html: unknown): string {
    return this.extractHtmlFromSafeValue(html);
  }

  onKeyDown(event: KeyboardEvent, index: number): void {
    if (!this.isBrowser) return;

    let newIndex: number;

    switch (event.key) {
      case 'ArrowLeft':
        newIndex = index > 0 ? index - 1 : this.tabs.length - 1;
        break;
      case 'ArrowRight':
        newIndex = index < this.tabs.length - 1 ? index + 1 : 0;
        break;
      case 'Home':
        newIndex = 0;
        break;
      case 'End':
        newIndex = this.tabs.length - 1;
        break;
      case 'Enter':
      case ' ':
        this.selectTab(index);
        event.preventDefault();
        return;
      default:
        return;
    }

    event.preventDefault();
    this.selectTab(newIndex);

    // Focus the new tab
    const newTabElement = document.getElementById(`tab-${newIndex}`);
    if (newTabElement) {
      newTabElement.focus();
    }
  }
}
